import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ChevronLeft, RefreshCw, ExternalLink } from "lucide-react";
import { formatReadTime } from "../lib/readingTime";
import { previewHeaders } from "../lib/previewAuth";
import { ArticleAudioPlayer } from "./ArticleAudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Head-tag helpers (no react-helmet dependency; touch head directly and clean up) ---
const upsertMeta = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
  return el;
};
const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
  return el;
};

const gatherBodyText = (perspective) => {
  const parts = [perspective.summary || ""];
  (perspective.body || []).forEach((b) => {
    if (b.type === "paragraph" && b.text) parts.push(b.text);
    if (b.type === "pullquote" && b.text) parts.push(b.text);
    if (b.type === "list" && Array.isArray(b.items)) parts.push(...b.items);
  });
  return parts.join(" ");
};

// --- Body block renderer ---
const Block = ({ block, t }) => {
  if (block.type === "heading") {
    const level = block.level || 2;
    const Tag = `h${Math.min(Math.max(level, 2), 4)}`;
    const cls = level === 2
      ? "text-2xl md:text-3xl font-serif tracking-tight text-primary-custom leading-tight mt-16 mb-6"
      : "text-xl md:text-2xl font-serif tracking-tight text-primary-custom leading-tight mt-12 mb-5";
    return (
      <div>
        <Tag className={cls}>{block.text}</Tag>
        {block.draft && (
          <p className="text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom/60 -mt-4 mb-6">
            {t("perspectives.draftSectionAwaiting")}
          </p>
        )}
      </div>
    );
  }
  if (block.type === "paragraph") {
    return (
      <p className="font-serif text-lg md:text-xl text-primary-custom leading-relaxed font-light mb-6">
        {block.text}
      </p>
    );
  }
  if (block.type === "pullquote") {
    return (
      <blockquote className="my-16 py-8 border-t border-b border-[var(--accent-bronze)]/40" data-testid="perspective-pullquote">
        <p className="text-2xl md:text-3xl lg:text-4xl font-serif italic text-primary-custom leading-snug tracking-tight max-w-3xl mx-auto text-center">
          &ldquo;{block.text}&rdquo;
        </p>
        {block.attribution && (
          <p className="mt-6 text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom text-center">— {block.attribution}</p>
        )}
      </blockquote>
    );
  }
  if (block.type === "image" || block.type === "figure") {
    return (
      <figure className="my-12">
        <div className="aspect-[16/10] overflow-hidden bg-surface-custom">
          {block.src
            ? <img src={block.src} alt={block.alt || ""} loading="lazy" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom/60">{block.type === "figure" ? "Figure — awaiting" : "Image — awaiting"}</div>}
        </div>
        {(block.caption || block.credit) && (
          <figcaption className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[11px] font-sans text-secondary-custom">
            {block.caption && <span className="italic font-serif">{block.caption}</span>}
            {block.credit && <span className="tracking-[0.2em] uppercase text-[10px] text-secondary-custom/70">— {block.credit}</span>}
          </figcaption>
        )}
      </figure>
    );
  }
  if (block.type === "list") {
    const Tag = block.style === "ordered" ? "ol" : "ul";
    const listCls = block.style === "ordered"
      ? "list-decimal pl-6 space-y-3 my-8 font-serif text-lg text-primary-custom leading-relaxed"
      : "list-disc pl-6 space-y-3 my-8 font-serif text-lg text-primary-custom leading-relaxed";
    return (
      <Tag className={listCls}>
        {(block.items || []).map((item, i) => <li key={i}>{item}</li>)}
      </Tag>
    );
  }
  return null;
};

export default function Perspective() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [perspective, setPerspective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const lang = (i18n.language || "en").split("-")[0];

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setPerspective(null);
    axios.get(`${API}/perspectives/${slug}?lang=${lang}`, { headers: previewHeaders() })
      .then(res => { if (!cancelled) setPerspective(res.data); })
      .catch(err => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, lang]);

  // Manage <head> — title, description, canonical, og:*, robots (noindex on draft)
  useEffect(() => {
    if (!perspective) return;
    const originalTitle = document.title;
    document.title = perspective.seo?.title || perspective.title;
    const desc = perspective.seo?.description || perspective.subtitle || "";
    const created = [];
    created.push(upsertMeta("name", "description", desc));
    created.push(upsertMeta("property", "og:title", perspective.seo?.title || perspective.title));
    created.push(upsertMeta("property", "og:description", desc));
    created.push(upsertMeta("property", "og:type", "article"));
    if (perspective.seo?.ogImage) created.push(upsertMeta("property", "og:image", perspective.seo.ogImage));
    // Canonical + og:url are set by the app-level <Canonical/> to the www host.
    // We only need canonical for JSON-LD's mainEntityOfPage below.
    const canonicalHref = `https://www.thethrivinglife101.com${perspective.seo?.canonical || `/perspectives/${perspective.slug}`}`;
    // Draft => noindex,nofollow; Published => index,follow
    created.push(upsertMeta("name", "robots", perspective.status === "draft" ? "noindex,nofollow" : "index,follow"));

    // Article schema (JSON-LD) — added only when published, avoids surfacing drafts
    let ld = null;
    if (perspective.status === "published") {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": perspective.title,
        "description": desc,
        "author": { "@type": perspective.author?.toLowerCase().includes("editorial") ? "Organization" : "Person", "name": perspective.author },
        "datePublished": perspective.publishedAt,
        "dateModified": perspective.updatedAt || perspective.publishedAt,
        "image": perspective.hero?.image || perspective.seo?.ogImage || undefined,
        "mainEntityOfPage": canonicalHref,
        "publisher": { "@type": "Organization", "name": "The Thriving Life 101" },
        "articleSection": perspective.category
      });
      document.head.appendChild(ld);
    }
    return () => {
      document.title = originalTitle;
      // Reset robots so other pages default to indexable
      upsertMeta("name", "robots", "index,follow");
      if (ld) document.head.removeChild(ld);
    };
  }, [perspective]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-secondary-custom">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-6 h-6 animate-spin text-[var(--accent-bronze)]" />
          <span className="font-serif italic text-sm">Gathering...</span>
        </div>
      </div>
    );
  }

  if (error || !perspective) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-secondary-custom px-6">
        <p className="font-serif italic">This perspective is not available.</p>
      </div>
    );
  }

  const isDraft = perspective.status === "draft";
  const publishedDate = perspective.publishedAt
    ? new Date(perspective.publishedAt).toLocaleDateString(lang, { year: "numeric", month: "long", day: "numeric" })
    : null;
  const updatedDate = perspective.updatedAt
    ? new Date(perspective.updatedAt).toLocaleDateString(lang, { year: "numeric", month: "long", day: "numeric" })
    : null;
  const readTime = perspective.status === "published"
    ? formatReadTime(gatherBodyText(perspective), t)
    : null;

  return (
    <article className="text-primary-custom" data-testid="perspective-page" data-status={perspective.status}>
      {/* Draft banner */}
      {isDraft && (
        <div className="bg-[var(--accent-bronze)]/10 border-b border-[var(--accent-bronze)]/30 py-3 px-6 text-center" data-testid="perspective-draft-banner">
          <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)]">
            {t("perspectives.draftBanner")}
          </span>
        </div>
      )}

      {/* Return link */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-10">
        <Link
          to={`/foundations/${perspective.categoryKey || "technology-human-potential"}`}
          data-testid="perspective-return-link"
          className="inline-flex items-center gap-2 text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors"
        >
          <ChevronLeft className="w-3 h-3" />
          <span>{perspective.category}</span>
        </Link>
      </div>

      {/* Hero header (editorial, restrained) */}
      <header className="max-w-4xl mx-auto px-6 md:px-12 pt-10 pb-14">
        <div className="flex items-center gap-3 text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold">
          <span>{t("perspectives.sectionLabel")}</span>
          <span className="w-6 h-px bg-[var(--accent-bronze)]/60" />
          <span>{perspective.editorialLabel}</span>
        </div>
        <h1 className="mt-6 font-serif tracking-tight text-primary-custom text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          {perspective.title}
        </h1>
        <p className="mt-6 font-serif text-lg md:text-xl italic text-secondary-custom leading-relaxed font-light max-w-3xl">
          {perspective.subtitle}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom">
          <span>{perspective.author}</span>
          {publishedDate && (<><span className="w-1 h-1 rounded-full bg-secondary-custom/50" /><span>{publishedDate}</span></>)}
          {updatedDate && updatedDate !== publishedDate && (<><span className="w-1 h-1 rounded-full bg-secondary-custom/50" /><span>{t("perspectives.updatedLabel")} · {updatedDate}</span></>)}
          {readTime && (<><span className="w-1 h-1 rounded-full bg-secondary-custom/50" /><span data-testid="perspective-read-time">{readTime}</span></>)}
        </div>

        {/* Article-length audio narration. Same architecture as other article
            page types; hidden entirely on drafts to keep the draft view quiet. */}
        {perspective.status === "published" && (
          <div className="mt-8 flex justify-center">
            <ArticleAudioPlayer
              text={gatherBodyText(perspective)}
              pageType="perspective"
              pageSlug={perspective.slug}
              testId="perspective-listen-article"
            />
          </div>
        )}
      </header>

      {/* Hero image / placeholder */}
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="aspect-[21/9] overflow-hidden bg-surface-custom border border-subtle-custom">
          {perspective.hero?.image ? (
            <img src={perspective.hero.image} alt={perspective.hero.alt || perspective.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-[#141210] text-[var(--accent-bronze)]/80" data-testid="perspective-hero-placeholder">
              <span className="text-[10px] font-sans tracking-[0.4em] uppercase">{t("perspectives.draftHeroLabel")}</span>
              <span className="mt-4 w-12 h-px bg-[var(--accent-bronze)]/60" />
              <span className="mt-4 text-sm font-serif italic text-[var(--accent-bronze)]/70 max-w-md text-center px-6">{t("perspectives.draftHeroCaption")}</span>
            </div>
          )}
        </div>
        {perspective.hero?.credit && (
          <p className="mt-3 text-[10px] font-sans tracking-[0.2em] uppercase text-secondary-custom">— {perspective.hero.credit}</p>
        )}
      </div>

      {/* Opening summary — visually distinct, comfortable */}
      {perspective.summary && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 pt-16 pb-8" data-testid="perspective-summary">
          <div className="border-l-2 border-[var(--accent-bronze)] pl-6 md:pl-8">
            {perspective.summary.split("\n\n").map((para, i) => (
              <p key={i} className="font-serif text-lg md:text-xl text-primary-custom leading-relaxed font-light mb-5 last:mb-0">
                {para}
              </p>
            ))}
            {perspective.summaryDraft && (
              <p className="mt-4 text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom/70">— {t("perspectives.draftBanner")}</p>
            )}
          </div>
        </section>
      )}

      {/* Article body */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="perspective-body">
        {(perspective.body || []).map((b, i) => <Block key={i} block={b} t={t} />)}
      </div>

      {/* Sources — hidden while empty */}
      {perspective.sources && perspective.sources.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 border-t border-subtle-custom" data-testid="perspective-sources">
          <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold block mb-8">
            {t("perspectives.sourcesHeading")}
          </span>
          <ol className="space-y-6">
            {perspective.sources.map((s, i) => (
              <li key={i} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom">{s.organisation}</span>
                  {s.publishedAt && <span className="text-[10px] font-sans text-secondary-custom/70">· {s.publishedAt}</span>}
                </div>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="font-serif text-base italic text-primary-custom hover:text-[var(--accent-bronze)] transition-colors inline-flex items-baseline gap-2">
                  <span>{s.title}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                {s.note && <p className="font-serif text-sm text-secondary-custom leading-relaxed">{s.note}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Continue Exploring — placeholders only until siblings exist */}
      {perspective.continueExploring?.placeholders?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 border-t border-subtle-custom" data-testid="perspective-continue">
          <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold block mb-10">
            {t(perspective.continueExploring.titleKey || "perspectives.continueExploringTechnology")}
          </span>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {perspective.continueExploring.placeholders.map((label, i) => (
              <div key={i} className="border-t border-subtle-custom pt-6 opacity-70">
                <span className="text-[9px] font-sans tracking-[0.3em] uppercase text-secondary-custom/70 block mb-4">Forthcoming</span>
                <h3 className="font-serif text-xl text-primary-custom leading-snug">{label}</h3>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
